declare var $: JQueryStatic;

import "core-js/actual/set/difference";
import { Hint } from "./hinter";
import {
  buildPracticeBoardAddress,
  getPracticeBoardFromPath,
} from "./practiceAddress";
import {
  getPracticeTutorialCompletion,
  getPracticeTutorialStep,
  stripPracticeStrategyPrefix,
} from "./practiceTutorial";
import { SudokuGame } from "./sudokuGame";

type PracticeBoard = {
  current: string[];
  solution: string[];
};

type PracticeCopy = {
  boardComplete: string;
  incorrectEntry: string;
  moveAdded: string;
  selectEmptyCell: string;
  strategyStepApplied: string;
  hintStepShown: string;
  strategyUnavailable: string;
  resetStrategy: string;
  lastMoveUndone: string;
  nothingToUndo: string;
  boardPosition: string;
  hintPosition: string;
  tutorialIntroduction: string;
  nextStep: string;
  goToNextBoard: string;
};

type PracticeConfig = {
  strategyKey: string;
  strategyName: string;
  strategySlug: string;
  practicePath: string;
  initialBoard: number;
  boards: PracticeBoard[];
  copy: PracticeCopy;
};

type HintState = {
  hint: Hint;
  steps: (() => string)[];
  currentStep: number;
  startingState: string;
};

export function formatPracticePosition(
  template: string,
  current: number,
  total: number,
) {
  return template
    .replace(/\{current\}/g, String(current))
    .replace(/\{total\}/g, String(total));
}

function parsePracticeBoard(serializedBoard: unknown): PracticeBoard {
  if (typeof serializedBoard !== "string") {
    throw new Error("Practice board must be serialized as text");
  }
  const separatorIndex = serializedBoard.indexOf(":");
  if (separatorIndex < 0) {
    throw new Error("Practice board is missing its solution");
  }
  const current = serializedBoard.slice(0, separatorIndex).split("|");
  const solution = serializedBoard.slice(separatorIndex + 1).split("|");
  if (current.length !== 81 || solution.length !== 81) {
    throw new Error("Practice board must contain 81 current and solved cells");
  }
  return { current, solution };
}

function getElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Missing practice page element: ${id}`);
  }
  return element as T;
}

function readConfig(): PracticeConfig {
  const configElement = getElement<HTMLScriptElement>("practice-config");
  const parsed = JSON.parse(configElement.textContent || "{}") as Record<
    string,
    unknown
  >;
  if (typeof parsed.strategyKey !== "string" || !parsed.strategyKey) {
    throw new Error("Practice strategy is missing");
  }
  if (typeof parsed.strategyName !== "string" || !parsed.strategyName) {
    throw new Error("Practice strategy name is missing");
  }
  if (typeof parsed.strategySlug !== "string" || !parsed.strategySlug) {
    throw new Error("Practice strategy slug is missing");
  }
  if (!Array.isArray(parsed.boards) || parsed.boards.length === 0) {
    throw new Error("Practice boards are missing");
  }
  if (!parsed.copy || typeof parsed.copy !== "object") {
    throw new Error("Practice copy is missing");
  }
  return {
    strategyKey: parsed.strategyKey,
    strategyName: parsed.strategyName,
    strategySlug: parsed.strategySlug,
    practicePath:
      typeof parsed.practicePath === "string" && parsed.practicePath
        ? parsed.practicePath
        : "/practice",
    initialBoard: Number.isInteger(parsed.initialBoard)
      ? Number(parsed.initialBoard)
      : 1,
    boards: parsed.boards.map(parsePracticeBoard),
    copy: parsed.copy as PracticeCopy,
  };
}

export function initializePractice() {
  const config = readConfig();
  const boards = config.boards;
  const copy = config.copy;
  const boardCounter = getElement<HTMLElement>("practice-board-counter");
  const status = getElement<HTMLElement>("practice-status");
  const previousBoardButton = getElement<HTMLAnchorElement>(
    "practice-prev-board",
  );
  const nextBoardButton = getElement<HTMLAnchorElement>("practice-next-board");
  const tutorialSlot = getElement<HTMLElement>("practice-tutorial-slot");
  const hintPanel = getElement<HTMLElement>("practice-hint-panel");
  const hintText = getElement<HTMLElement>("practice-hint-text");
  const hintCounter = getElement<HTMLElement>("practice-hint-counter");
  const previousHintButton =
    getElement<HTMLButtonElement>("practice-prev-hint");
  const nextHintButton = getElement<HTMLButtonElement>("practice-next-hint");
  const restartTutorialButton = getElement<HTMLButtonElement>(
    "practice-restart-tutorial",
  );
  const candidateMode = getElement<HTMLInputElement>("practice-candidate-mode");
  const sudokuTable = getElement<HTMLTableElement>("sudokutable");

  const requestedBoard =
    getPracticeBoardFromPath(window.location.pathname) || config.initialBoard;
  let currentBoardIndex = Math.min(
    Math.max(requestedBoard - 1, 0),
    boards.length - 1,
  );
  let game: SudokuGame;
  let hintState: HintState | null = null;

  function setStatus(
    message: string,
    tone: "muted" | "success" | "danger" = "muted",
  ) {
    status.hidden = false;
    status.textContent = message;
    status.classList.toggle("text-muted", tone === "muted");
    status.classList.toggle("text-success", tone === "success");
    status.classList.toggle("text-danger", tone === "danger");
  }

  function clearStatus() {
    status.hidden = true;
    status.textContent = "";
    status.classList.remove("text-success", "text-danger");
    status.classList.add("text-muted");
  }

  function getBoardAddress(boardIndex: number) {
    return buildPracticeBoardAddress(
      config.practicePath,
      window.location.href,
      config.strategySlug,
      boardIndex,
    );
  }

  function updateAddress(historyMode: "push" | "replace") {
    const method = historyMode === "push" ? "pushState" : "replaceState";
    window.history[method]({}, "", getBoardAddress(currentBoardIndex));
  }

  function updatePagination() {
    boardCounter.textContent = formatPracticePosition(
      copy.boardPosition,
      currentBoardIndex + 1,
      boards.length,
    );
    const updateBoardLink = (
      link: HTMLAnchorElement,
      boardIndex: number,
      disabled: boolean,
    ) => {
      link.classList.toggle("disabled", disabled);
      link.setAttribute("aria-disabled", String(disabled));
      if (disabled) {
        link.removeAttribute("href");
      } else {
        const url = getBoardAddress(boardIndex);
        link.setAttribute("href", `${url.pathname}${url.search}`);
      }
    };
    updateBoardLink(
      previousBoardButton,
      currentBoardIndex - 1,
      currentBoardIndex === 0,
    );
    updateBoardLink(
      nextBoardButton,
      currentBoardIndex + 1,
      currentBoardIndex === boards.length - 1,
    );
  }

  function clearGamePresentation(
    clearHighlights = true,
    clearSelection = false,
  ) {
    const highlightsCleared =
      clearHighlights && Boolean(game?.clearTemporaryHighlights());
    const selectionCleared =
      clearSelection && Boolean(game?.clearActiveSelection());
    if (highlightsCleared || selectionCleared) {
      game.updateGameBoardUI(true);
    }
  }

  function clearHint(
    options: {
      hide?: boolean;
      clearHighlights?: boolean;
      clearSelection?: boolean;
    } = {},
  ) {
    const {
      hide = true,
      clearHighlights = true,
      clearSelection = false,
    } = options;
    hintState = null;
    hintText.textContent = "";
    hintCounter.textContent = "";
    previousHintButton.disabled = true;
    nextHintButton.disabled = true;
    nextHintButton.textContent = copy.nextStep;
    restartTutorialButton.hidden = true;
    if (hide) {
      if (typeof $ === "function") {
        $(tutorialSlot).stop(true, true).hide();
      } else {
        tutorialSlot.style.display = "none";
      }
      tutorialSlot.hidden = true;
      hintPanel.hidden = true;
    }
    clearGamePresentation(clearHighlights, clearSelection);
  }

  function configureGame(board: PracticeBoard) {
    if (game) {
      game.loadPuzzle(board.solution, board.current);
      game.undos = [];
      game.activeCell = null;
      game.activeNumber = null;
    } else {
      game = new SudokuGame(9, board.solution, board.current);
    }
    game.possibilitiesInGrid = true;
    game.highlightActiveCell = true;
    game.highlightRelatedCells = true;
    game.highlightActiveNumber = "false";
    game.automaticallyRemovePossibilities = true;
    game.updateGameBoardUI();
  }

  function loadBoard(
    index: number,
    historyMode: "push" | "replace" | false = "replace",
  ) {
    if (index < 0 || index >= boards.length) {
      return;
    }
    currentBoardIndex = index;
    clearHint();
    configureGame(boards[currentBoardIndex]);
    updatePagination();
    candidateMode.checked = false;
    clearStatus();
    if (historyMode) {
      updateAddress(historyMode);
    }
  }

  function selectCell(cellId: number) {
    const cell = game.getCell(cellId);
    if (!cell || cell.isGiven) {
      return;
    }
    game.setActiveCell(cellId);
    game.activeNumber = cell.guess;
    game.updateGameBoardUI();
    sudokuTable.focus({ preventScroll: true });
  }

  function updateAfterMove(
    defaultMessage = copy.moveAdded,
    defaultTone: "muted" | "success" = "muted",
  ) {
    game.updateGameBoardUI();
    if (game.isComplete()) {
      setStatus(copy.boardComplete, "success");
    } else if (game.hasErrors()) {
      setStatus(copy.incorrectEntry, "danger");
    } else {
      setStatus(defaultMessage, defaultTone);
    }
  }

  function enterValue(value: string) {
    const activeCell = game.activeCell;
    if (!activeCell || activeCell.isGiven) {
      setStatus(copy.selectEmptyCell);
      return;
    }

    clearHint();
    game.saveUndo();
    if (value === "clear") {
      game.clearCell(activeCell.id);
      game.activeNumber = null;
    } else if (candidateMode.checked) {
      game.togglePossibility(activeCell.id, value);
      game.activeNumber = value;
    } else {
      game.addGuess(activeCell.id, value);
      game.activeNumber = value;
    }
    updateAfterMove();
  }

  function findStrategyHint(): Hint | null {
    game.resetPossibilitiesToBaseline(boards[currentBoardIndex].current);
    const hint = game.hint([config.strategyKey]);
    return hint?.strategy === config.strategyKey ? hint : null;
  }

  function buildHintState(startingState: string): HintState | null {
    const hint = findStrategyHint();
    if (!hint?.description) {
      return null;
    }
    return {
      hint,
      steps: hint.description(hint),
      currentStep: -1,
      startingState,
    };
  }

  function revealTutorial() {
    tutorialSlot.hidden = false;
    const scrollToTutorial = () => {
      hintPanel.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (typeof $ !== "function") {
      tutorialSlot.style.display = "";
      scrollToTutorial();
      return;
    }
    const slot = $(tutorialSlot).stop(true, true);
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      slot.show();
      scrollToTutorial();
      return;
    }
    slot.hide().slideDown(200, scrollToTutorial);
  }

  function renderHintIntroduction() {
    if (!hintState) {
      return;
    }
    hintState.currentStep = -1;
    hintText.textContent = copy.tutorialIntroduction;
    hintCounter.textContent = "";
    previousHintButton.disabled = true;
    nextHintButton.disabled = false;
    nextHintButton.textContent = copy.nextStep;
    restartTutorialButton.hidden = true;
    clearStatus();
  }

  function renderHintStep(stepIndex: number) {
    if (!hintState || stepIndex < 0 || stepIndex >= hintState.steps.length) {
      return;
    }

    hintState.currentStep = stepIndex;
    const stateBeforeStep = game.serialize();
    const text = hintState.steps[stepIndex]();
    const stepChangedBoard = game.serialize() !== stateBeforeStep;
    // SudokuGame's internal solver owns this trusted explanatory markup; no
    // user- or CMS-provided content reaches this sink.
    hintText.innerHTML =
      stepIndex === 0
        ? stripPracticeStrategyPrefix(text, config.strategyName)
        : text;
    hintCounter.textContent = formatPracticePosition(
      copy.hintPosition,
      stepIndex + 1,
      hintState.steps.length,
    );
    const completion = getPracticeTutorialCompletion(
      stepIndex,
      hintState.steps.length,
      currentBoardIndex < boards.length - 1,
    );
    previousHintButton.disabled = false;
    nextHintButton.disabled = completion.nextDisabled;
    nextHintButton.textContent = completion.isComplete
      ? copy.goToNextBoard
      : copy.nextStep;
    restartTutorialButton.hidden = !completion.isComplete;
    if (completion.isComplete) {
      if (stepChangedBoard) {
        updateAfterMove(copy.strategyStepApplied, "success");
      } else {
        setStatus(copy.hintStepShown);
      }
    } else {
      setStatus(copy.hintStepShown);
    }
  }

  function showHint() {
    if (hintState) {
      hintPanel.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (game.hasErrors()) {
      clearHint();
      setStatus(copy.incorrectEntry, "danger");
      return;
    }

    clearHint({ hide: false, clearSelection: true });
    const startingState = game.serialize();
    const undoCount = game.undos.length;
    game.saveUndo();
    hintState = buildHintState(startingState);
    hintPanel.hidden = false;

    if (!hintState || hintState.steps.length === 0) {
      game.restoreGame(startingState, true);
      game.undos.length = undoCount;
      game.updateGameBoardUI();
      hintText.textContent = copy.strategyUnavailable;
      hintCounter.textContent = "";
      setStatus(copy.resetStrategy);
      revealTutorial();
      return;
    }

    game.updateGameBoardUI();
    renderHintIntroduction();
    revealTutorial();
  }

  function moveHint(direction: number) {
    if (!hintState) {
      return;
    }
    const nextStep = getPracticeTutorialStep(
      hintState.currentStep,
      direction,
      hintState.steps.length,
    );
    if (nextStep === null) {
      return;
    }

    const startingState = hintState.startingState;
    game.restoreGame(startingState, true);
    clearGamePresentation(true, true);
    hintState = buildHintState(startingState);
    if (!hintState) {
      showHint();
      return;
    }

    if (hintState.steps.length === 0) {
      hintText.textContent = copy.strategyUnavailable;
      hintCounter.textContent = "";
      previousHintButton.disabled = true;
      nextHintButton.disabled = true;
      nextHintButton.textContent = copy.nextStep;
      restartTutorialButton.hidden = true;
      setStatus(copy.resetStrategy);
      return;
    }

    if (nextStep === -1) {
      game.updateGameBoardUI();
      renderHintIntroduction();
      return;
    }

    const targetStep = Math.min(nextStep, hintState.steps.length - 1);
    for (let stepIndex = 0; stepIndex < targetStep; stepIndex += 1) {
      hintState.steps[stepIndex]();
      clearGamePresentation(true, true);
    }
    renderHintStep(targetStep);
  }

  sudokuTable.addEventListener("click", (event) => {
    const target = (event.target as HTMLElement).closest<HTMLTableCellElement>(
      "td[id^='T']",
    );
    if (!target) {
      return;
    }
    const cellId = Number(target.id.slice(1));
    if (Number.isInteger(cellId)) {
      selectCell(cellId);
    }
  });

  sudokuTable.addEventListener("keydown", (event) => {
    if (/^[1-9]$/.test(event.key)) {
      event.preventDefault();
      enterValue(event.key);
      return;
    }
    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      enterValue("clear");
      return;
    }

    const directionByKey: Record<string, string> = {
      ArrowUp: "up",
      ArrowDown: "down",
      ArrowLeft: "left",
      ArrowRight: "right",
    };
    const direction = directionByKey[event.key];
    if (direction) {
      event.preventDefault();
      game.moveActiveCell(direction);
      game.updateGameBoardUI();
    }
  });

  document
    .querySelectorAll<HTMLButtonElement>("[data-practice-number]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        enterValue(button.dataset.practiceNumber || "");
        sudokuTable.focus({ preventScroll: true });
      });
    });

  const shouldNavigateInPlace = (event: MouseEvent) =>
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey;

  previousBoardButton.addEventListener("click", (event) => {
    if (!shouldNavigateInPlace(event)) {
      return;
    }
    event.preventDefault();
    loadBoard(currentBoardIndex - 1, "push");
  });
  nextBoardButton.addEventListener("click", (event) => {
    if (!shouldNavigateInPlace(event)) {
      return;
    }
    event.preventDefault();
    loadBoard(currentBoardIndex + 1, "push");
  });
  window.addEventListener("popstate", () => {
    const boardNumber = getPracticeBoardFromPath(window.location.pathname) || 1;
    loadBoard(Math.min(Math.max(boardNumber - 1, 0), boards.length - 1), false);
  });
  getElement<HTMLButtonElement>("practice-show-hint").addEventListener(
    "click",
    showHint,
  );
  previousHintButton.addEventListener("click", () => moveHint(-1));
  nextHintButton.addEventListener("click", () => {
    if (
      hintState &&
      getPracticeTutorialCompletion(
        hintState.currentStep,
        hintState.steps.length,
        currentBoardIndex < boards.length - 1,
      ).isComplete
    ) {
      loadBoard(currentBoardIndex + 1, "push");
      return;
    }
    moveHint(1);
  });
  restartTutorialButton.addEventListener("click", () => {
    loadBoard(currentBoardIndex, false);
    showHint();
  });

  getElement<HTMLButtonElement>("practice-reset").addEventListener(
    "click",
    () => {
      loadBoard(currentBoardIndex, false);
    },
  );

  getElement<HTMLButtonElement>("practice-undo").addEventListener(
    "click",
    () => {
      if (!game.hasUndos()) {
        setStatus(copy.nothingToUndo);
        return;
      }
      clearHint();
      const stateBeforeUndo = game.serialize();
      game.undo();
      game.updateGameBoardUI();
      setStatus(
        game.serialize() === stateBeforeUndo
          ? copy.nothingToUndo
          : copy.lastMoveUndone,
      );
    },
  );

  loadBoard(currentBoardIndex, "replace");
}

export function bootstrapPractice() {
  try {
    initializePractice();
    return true;
  } catch (error) {
    const status = document.getElementById("practice-status");
    if (status) {
      status.hidden = false;
      status.textContent =
        status.dataset.loadError ||
        "Practice could not be loaded. Please refresh the page.";
      status.classList.remove("text-muted", "text-success");
      status.classList.add("text-danger");
    }
    console.error("Unable to initialize strategy practice", error);
    return false;
  }
}

bootstrapPractice();
